import type { ComponentProps } from 'react';
import type { UseRejectionReasonsResult } from '../../../hooks/useRejectionReasons';
import { cn } from '../../../lib/utils';
import {
  approveActionClassName,
  rejectActionClassName,
  secondaryActionClassName,
} from '../controlStyles';
import {
  MIN_REJECTION_NOTE_LENGTH,
  type UseModeratorActionsResult,
} from '../hooks/useModeratorActions';
import { HubspotTicketStatusEditor } from '../status/HubspotTicketStatusEditor';
import { RejectSubmissionDialog } from '../status/RejectSubmissionDialog';

interface HubspotTicketActionBarProps {
  isModerator: boolean;
  statusEditor: ComponentProps<typeof HubspotTicketStatusEditor>;
  moderatorActions?: UseModeratorActionsResult;
  rejectionReasonState: UseRejectionReasonsResult;
  isChecklistBlocking: boolean;
  checklistProgress: { completed: number; total: number } | null;
  className?: string;
}

/**
 * The decision controls. Moderators approve/reject; admins move the stage.
 * Rendered in the header on wide screens and as a sticky bottom bar on phones.
 */
export function HubspotTicketActionBar({
  isModerator,
  statusEditor,
  moderatorActions,
  rejectionReasonState,
  isChecklistBlocking,
  checklistProgress,
  className,
}: HubspotTicketActionBarProps) {
  if (!isModerator || !moderatorActions) {
    return (
      <div className={cn('text-sm', className)}>
        <HubspotTicketStatusEditor {...statusEditor} />
      </div>
    );
  }

  const {
    isApproving,
    isRejecting,
    rejectionNote,
    rejectionReasons,
    isRejectSubmitting,
    isRejectSubmitDisabled,
    isSelfAssigning,
    isAssignedToSelf,
    moderatorError,
    approvedStageFound,
    canApprove,
    canRejectDirectly,
    canReassignTickets,
    setRejectionNote,
    toggleRejectionReason,
    handleApprove,
    handleStartReject,
    handleCancelReject,
    handleSubmitReject,
    handleSelfAssign,
  } = moderatorActions;
  const isBusy = isApproving || isRejectSubmitting || isSelfAssigning;

  // The dialog's multi-select emits the full next selection; bridge it to the
  // hook's single-code toggle without changing the hook's API.
  const handleReasonsChange = (next: string[]) => {
    const current = new Set(rejectionReasons);
    const nextSet = new Set(next);
    rejectionReasons.forEach((code) => {
      if (!nextSet.has(code)) {
        toggleRejectionReason(code);
      }
    });
    next.forEach((code) => {
      if (!current.has(code)) {
        toggleRejectionReason(code);
      }
    });
  };

  return (
    <div className={cn('flex flex-col gap-1.5 text-sm', className)}>
      <div className="flex items-center gap-2">
        {!canReassignTickets && (
          <button
            type="button"
            onClick={() => {
              void handleSelfAssign();
            }}
            disabled={isBusy}
            className={secondaryActionClassName}
          >
            {isSelfAssigning
              ? 'Saving…'
              : isAssignedToSelf
                ? 'Unassign'
                : 'Assign to me'}
          </button>
        )}
        <div className="ml-auto flex flex-1 items-center justify-end gap-2 sm:flex-none">
          <button
            type="button"
            onClick={handleStartReject}
            disabled={isBusy}
            className={`${rejectActionClassName} flex-1 sm:flex-none`}
          >
            Reject
          </button>
          {canApprove && (
            <button
              type="button"
              onClick={() => {
                void handleApprove();
              }}
              disabled={isBusy || isChecklistBlocking || !approvedStageFound}
              title={
                isChecklistBlocking
                  ? 'Complete the review checklist to approve'
                  : undefined
              }
              className={`${approveActionClassName} flex-1 sm:flex-none`}
            >
              {isApproving ? 'Approving…' : 'Approve'}
            </button>
          )}
        </div>
      </div>

      {isChecklistBlocking && checklistProgress && (
        <div className="text-xs text-amber-300/90">
          Review checklist {checklistProgress.completed}/
          {checklistProgress.total} · finish it in Overview to approve.
        </div>
      )}
      {moderatorError && (
        <div className="text-xs text-red-400">{moderatorError}</div>
      )}

      <RejectSubmissionDialog
        open={isRejecting}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            handleStartReject();
          } else {
            handleCancelReject();
          }
        }}
        mode={canRejectDirectly ? 'hard' : 'soft'}
        reasons={rejectionReasonState.rejectionReasonOptions}
        isReasonsLoading={rejectionReasonState.isRejectionReasonsLoading}
        reasonsError={rejectionReasonState.rejectionReasonsError}
        selectedCodes={rejectionReasons}
        onSelectedCodesChange={handleReasonsChange}
        note={rejectionNote}
        onNoteChange={setRejectionNote}
        onSubmit={() => {
          void handleSubmitReject();
        }}
        isSubmitting={isRejectSubmitting}
        submitDisabled={isRejectSubmitDisabled}
        noteMinLength={MIN_REJECTION_NOTE_LENGTH}
        error={moderatorError}
      />
    </div>
  );
}
